import { Link, NavigationPage } from '@/app/components'
import LinkDiagram from '@/app/features/logos/LinkDiagram'
import TeslaLogo from '@/authenticated/images/tesla.webp'
import OptiwattIconLogo from '@/app/img/logo-icon-colored-filled.webp'
import { Text } from '@/components'
import {
  useAppDispatch,
  useAppSelector,
  useMockableViewModel,
  useUrlSearchParam,
} from '@/hooks'
import { useHistory, useParams } from 'react-router-dom'
import KeyConfirmationStatusModal, { StatusType } from './status'
import { ComponentProps, useState } from 'react'
import {
  GET_VEHICLE_UPDATE_TESLA_PUBLIC_KEY_SUCCESS,
  getVehicleWithUpdatedTeslaPublicKey,
} from '@/actions/vehicles'
import { Vehicle } from '@/types'
import { teslaFleetAuthenticationCollection } from '@/reducers/teslaFleetAuthentication'
import { useNavigateToFleetPublicKey } from '../utils/useNavigateToFleetPublicKey'
import { logException } from '@/logging/sentry'
import { useNavigation } from '@/app/hooks'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import useNavigateToTeslaKeyPairing from '@/app/hooks/useNavigateToTeslaKeyPairing'

type ConnectionStatus = ComponentProps<
  typeof KeyConfirmationStatusModal
>['mode']

export type ViewModelStatus =
  | 'initial'
  | 'loading'
  | 'api_failure'
  | 'key_not_attached'
  | 'success'
export type UrlStatusParam = StatusType | 'initial' | 'key_not_attached' | null

export const useViewModel = useMockableViewModel({
  useViewModel() {
    const dispatch = useAppDispatch()
    const history = useHistory()
    const { data: fleetAuthentications, status: fleetAuthenticationsStatus } =
      teslaFleetAuthenticationCollection.useFetch()
    const { vehicleId } = useParams<{ vehicleId: string | undefined }>()
    const allVehicles = useAppSelector((state) => state.vehicles.vehicles ?? [])
    const [checkedVehicles, setCheckedVehicles] = useState<Vehicle[]>([])

    const statusParam = useUrlSearchParam('status') as UrlStatusParam

    const getViewModelStatusFromUrl = (
      param: UrlStatusParam,
    ): ViewModelStatus => {
      switch (param) {
        case 'connecting':
          return 'loading'
        case 'connected':
          return 'success'
        case 'failed':
          return 'api_failure'
        case 'key_not_attached':
          return 'key_not_attached'
        case 'initial':
          return 'initial'
        default:
          return 'initial'
      }
    }

    const [currentStatus, setCurrentStatus] = useState<ViewModelStatus>(
      getViewModelStatusFromUrl(statusParam),
    )

    let vehicles =
      (useAppSelector((state) => state.vehicleAuth.mfaData ?? []) as
        | Vehicle[]
        | undefined) ?? []
    const navigate = useNavigation()
    const { inFlow, popFlowUri } = useMultiProgramFlowControl()

    if (vehicleId) {
      vehicles = allVehicles.filter((v) => v.id === Number.parseInt(vehicleId))
    }

    const updateStatus = (newVmStatus: ViewModelStatus) => {
      setCurrentStatus(newVmStatus)
      let urlQueryParam: UrlStatusParam = null
      switch (newVmStatus) {
        case 'initial':
          urlQueryParam = 'initial'
          break
        case 'loading':
          urlQueryParam = 'connecting'
          break
        case 'api_failure':
          urlQueryParam = 'failed'
          break
        case 'key_not_attached':
          urlQueryParam = 'key_not_attached'
          break
        case 'success':
          urlQueryParam = 'connected'
          break
      }
      if (urlQueryParam) {
        history.replace({ search: `?status=${urlQueryParam}` })
      } else {
        history.replace({ search: '' })
      }
    }

    const clearStatus = () => {
      updateStatus('initial')
    }

    const onFailForModal = () => {
      updateStatus('api_failure')
    }

    const onPageExit = () => {
      if (inFlow) {
        const multiProgramUri = popFlowUri()
        if (multiProgramUri) {
          navigate.push(multiProgramUri)
          return
        }
      }
      navigate.push('/app')
    }

    const retryFetch = async (vehicleId: number, retries = 1): Promise<any> => {
      try {
        const response = await dispatch(
          getVehicleWithUpdatedTeslaPublicKey(vehicleId),
        )
        if (response.type === GET_VEHICLE_UPDATE_TESLA_PUBLIC_KEY_SUCCESS) {
          return response
        } else {
          if (retries > 0) {
            return await retryFetch(vehicleId, retries - 1)
          } else {
            throw new Error('Max retries reached')
          }
        }
      } catch (error) {
        if (retries > 0) {
          return await retryFetch(vehicleId, retries - 1)
        } else {
          throw error
        }
      }
    }

    const onConfirmClick = async () => {
      updateStatus('loading')
      try {
        const responses = await Promise.all(
          vehicles?.map((v) => retryFetch(v.id)),
        )
        const vehiclesPayload = responses.map((r) => r.payload) as
          | Vehicle[]
          | undefined
        setCheckedVehicles(vehiclesPayload ?? [])

        if (!vehiclesPayload || vehiclesPayload.length === 0) {
          updateStatus('api_failure')
          return
        }

        const anyKeyNotAttached = vehiclesPayload.some(
          (v) => v.car?.public_key_not_attached_at,
        )

        if (anyKeyNotAttached) {
          updateStatus('key_not_attached')
        } else {
          updateStatus('success')
        }
      } catch (error) {
        logException(error, { logToConsole: true })
        updateStatus('api_failure')
      }
    }

    const { navigate: navigateToTeslaKeyPairing } =
      useNavigateToTeslaKeyPairing()

    const retryKeyConnection = useNavigateToFleetPublicKey({
      fleetPublicKeyUrl: fleetAuthentications[0]?.public_key_url,
    })

    const restartKeyConnectionFlow = (vehicleId: string) => {
      navigateToTeslaKeyPairing(
        vehicles.find((v) => v.id === Number.parseInt(vehicleId)) ??
          vehicles[0],
      )
    }

    let modalStatus: null | ConnectionStatus = null
    if (currentStatus === 'loading') {
      modalStatus = 'connecting'
    } else if (currentStatus === 'success') {
      modalStatus = 'connected'
    }

    return {
      onConfirmClick: onConfirmClick,
      onClose: clearStatus,
      vehicles: checkedVehicles,
      retryKeyConnection: () => retryKeyConnection(vehicleId ?? ''),
      restartKeyConnectionFlow: () => restartKeyConnectionFlow(vehicleId ?? ''),
      isLoading:
        ['loading', 'idle'].includes(fleetAuthenticationsStatus) ||
        currentStatus === 'loading',
      status: currentStatus,
      modalDisplayStatus: modalStatus,
      onFailForModal,
      onPageExit,
    }
  },
  useMockViewModel() {
    return {
      onConfirmClick: () => {},
      onClose: () => {},
      vehicles: [] as Vehicle[],
      retryKeyConnection: () => {},
      restartKeyConnectionFlow: () => {},
      isLoading: false,
      status: 'initial' as ViewModelStatus,
      modalDisplayStatus: null as null | ConnectionStatus,
      onFailForModal: () => {},
      onPageExit: () => {},
    }
  },
})
